import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TranslationNS } from '@gokrew/models/constants';
import { CompanySwitcher } from '@gokrew/ui';
import { AppLoaderPage } from '@gokrew/ui/components/AppLoaderPage';
import SlackLinkPage from '@gokrew/ui/components/SlackLinkPage';
import { linkAdminSlackAccount } from '@/api/slack-link';
import { useAuth } from '@/context/AuthContext';
import type { Company } from '@gokrew/models/company';
import { Button } from '@/components/ui/CustomButton';
import { SlackLogo } from '@gokrew/ui/components/SlackLogo';
import { getInitialsFromName } from '@gokrew/ui/utility/utility';
import styles from './SlackLinkPage.module.css';
import { companyDisplayName } from '@gokrew/ui/utility/companyDisplayName';

export default function AdminSlackLinkPage() {
  const { t } = useTranslation(TranslationNS.slack);
  const { companyId, companyName, selectedCompany, companies, isLoading } =
    useAuth();
  const [previewCompanyId, setPreviewCompanyId] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);

  // Companies are still resolving — wait rather than firing the link request
  // with a possibly stale/null companyId from a previous session.
  if (isLoading) {
    return <AppLoaderPage />;
  }

  const companyList = companies ?? [];
  const needsSelection = companyList.length > 1;

  if (needsSelection && !confirmed) {
    const selectedId = previewCompanyId ?? companyId ?? companyList[0].id;
    const preview =
      companyList.find((c) => c.id === selectedId) ?? companyList[0];

    return (
      <div className={styles.wrapper}>
        <div className={styles.card}>
          <div className={styles.header}>
            <SlackLogo size={45} />
            <span className={styles.headerDivider} />
            {preview.logo ? (
              <img
                src={preview.logo}
                alt={companyDisplayName(preview)}
                className={styles.companyLogo}
              />
            ) : (
              <div className={styles.companyInitial}>
                {getInitialsFromName(companyDisplayName(preview))}
              </div>
            )}
          </div>

          <h2 className={styles.title}>
            {t('adminSlackLink.selectCompany.title')}
          </h2>
          <p className={styles.description}>
            {t('adminSlackLink.selectCompany.description')}
          </p>

          <CompanySwitcher
            companies={companyList}
            selectedCompanyId={selectedId}
            onCompanyChange={(company: Company) =>
              setPreviewCompanyId(company.id)
            }
            className={styles.switcher}
          />

          <Button
            className={styles.confirmButton}
            onClick={() => setConfirmed(true)}
          >
            {t('adminSlackLink.selectCompany.confirm_button')}
          </Button>
        </div>
      </div>
    );
  }

  const activeCompanyId = previewCompanyId ?? companyId ?? companyList[0]?.id;
  const activeCompany =
    companyList.find((c) => c.id === activeCompanyId) ?? selectedCompany;

  return (
    <SlackLinkPage
      onLink={(token) => linkAdminSlackAccount(activeCompanyId, token)}
      dashboardPath="/dashboard"
      i18nPrefix="adminSlackLink"
      companyName={
        activeCompany ? companyDisplayName(activeCompany) : companyName
      }
      companyLogoUrl={activeCompany?.logo ?? null}
    />
  );
}
